import React, { useRef, useState, useEffect, useCallback } from "react"

import { useQueryClient } from "@tanstack/react-query"

import usePageConfig from "Pods/Shared/Hooks/usePageConfig"
import usePersistCameraPose from "Pods/ThreeDStudio/Hooks/usePersistCameraPose"
import LoadingState from "Pods/ThreeDStudio/Shared/LoadingState/LoadingState"
import { useViewerContext } from "Pods/ThreeDStudio/Shared/ViewerContext/ViewerContext"
import { useThreeDStudioStore } from "Pods/ThreeDStudio/Store"
import { setViewerFileFormat } from "Pods/ThreeDStudio/Store/actions/viewer"

import EnvironmentSettings from "./Components/EnvironmentSettings/EnvironmentSettings"
import {
  BACKGROUND_IMAGES_QUERY_KEY,
  fetchBackgroundImages,
} from "./Components/EnvironmentSettings/Hooks/useBackgroundImages"
import InnerViewerSidebar from "./Components/InnerViewerSidebar/InnerViewerSidebar"
import ModelDetailsCard from "./Components/ModelDetailsCard/ModelDetailsCard"
import RenderModeSelector from "./Components/RenderModeSelector/RenderModeSelector"
import useViewerHotkeys from "./Hooks/useViewerHotkeys"
import { applyInitialSettings } from "./Utils/applyInitialSettings"
import { applyTopologyMode } from "./Utils/applyTopologyMode"

function ViewerContent() {
  const [isEnvironmentSettingsOpen, setIsEnvironmentSettingsOpen] = useState(false)
  const handleEnvironmentSettingsClick = useCallback(() => {
    setIsEnvironmentSettingsOpen(prev => !prev)
  }, [])
  useViewerHotkeys({ onToggleEnvironmentSettings: handleEnvironmentSettingsClick })
  const queryClient = useQueryClient()
  const { isCurrentUser: isLoggedIn } = usePageConfig() || {}
  const [isInitialApplyPending, setIsInitialApplyPending] = useState(true)
  const viewer = useViewerContext()
  usePersistCameraPose(viewer)
  const uploadedModelUrl = useThreeDStudioStore(state => state.modelSettings.inputThreeDUrl)
  const uploadedModelFormat = useThreeDStudioStore(state => state.viewer.fileFormat)
  const modelUrl = useThreeDStudioStore(state => state.ui.openedModel?.outputAssets?.[0]?.fileUrl)
  const materialPngUrl = useThreeDStudioStore(
    state => state.ui.openedModel?.runExtraOutputs?.find(output => output.file_type === 'png')?.fileUrl
  )
  const materialMtlUrl = useThreeDStudioStore(
    state => state.ui.openedModel?.runExtraOutputs?.find(output => output.file_type === 'mtl')?.fileUrl
  )
  const settings = useThreeDStudioStore(state => state.ui?.openedModel?.settings)
  const material = useThreeDStudioStore(state => state.viewer?.material)
  const openedModelId = useThreeDStudioStore(state => state.ui?.openedModel?.providerModelId)
  const isSmartLowPoly = useThreeDStudioStore(
    state => state.modelSettings.isSmartLowPoly || state.remeshSettings.isSmartLowPoly
  )

  const modelData = uploadedModelUrl
    ? { url: uploadedModelUrl, format: uploadedModelFormat }
    : { url: modelUrl, pngUrl: materialPngUrl, mtlUrl: materialMtlUrl }

  const currentLoadRequestIdRef = useRef(0)
  const currentModelIdRef = useRef(null)
  // Serializes viewer mutations. `loadModel` adds the mesh to the scene before
  // it resolves (and before the model lands in `getLoadedModels`), so a newer
  // request's `unloadAllModels` cannot remove an older mesh that is still
  // mid-load. Chaining each request guarantees only one load runs at a time, so
  // two models can never paint at once when the user switches rapidly.
  const loadChainRef = useRef(Promise.resolve())

  useEffect(() => {
    if (!viewer || viewer.isDisposed) {
      return
    }

    const currentViewer = viewer
    const { url, pngUrl, mtlUrl, format } = modelData
    const urlToLoad = (() => {
      const files = [url, pngUrl, mtlUrl].filter(Boolean)
      if (files.length === 1) {
        return files[0]
      }

      return files
    })()

    const requestId = currentLoadRequestIdRef.current + 1
    currentLoadRequestIdRef.current = requestId

    if (!urlToLoad) {
      loadChainRef.current = loadChainRef.current.then(() => {
        if (currentLoadRequestIdRef.current !== requestId || currentViewer.isDisposed) {
          return
        }
        try {
          if (currentModelIdRef.current) {
            currentViewer.unloadModel(currentModelIdRef.current)
            currentModelIdRef.current = null
          } else {
            currentViewer.unloadAllModels()
          }
        } catch {
          // Ignore unload failures; viewer state will be corrected on next update.
        }
        setIsInitialApplyPending(false)
      }).catch(() => {})
      return
    }

    setIsInitialApplyPending(true)

    loadChainRef.current = loadChainRef.current.then(() => {
      // Superseded before this request reached the head of the chain: skip the
      // load entirely so rapid switching only ever loads the latest model.
      if (currentLoadRequestIdRef.current !== requestId || currentViewer.isDisposed) {
        return undefined
      }

      try {
        currentViewer.unloadAllModels()
        currentModelIdRef.current = null
      } catch {
        // Ignore unload failures; viewer state will be corrected on next update.
      }

      return Promise.all([
        currentViewer.loadModel(urlToLoad, undefined, format),
        // Resolves the cached background-images list (fetches once if missing)
        // so applyInitialSettings can map surroundingId -> background_url.
        // Falls back to [] on failure to keep the rest of the apply unblocked.
        // The endpoint requires an account, so logged-out users skip the fetch
        // and apply with an empty list.
        isLoggedIn
          ? queryClient.ensureQueryData({
            queryKey: BACKGROUND_IMAGES_QUERY_KEY,
            queryFn: fetchBackgroundImages,
          }).catch(() => [])
          : Promise.resolve([]),
      ])
        .then(([modelEntry, images]) => {
          if (currentLoadRequestIdRef.current !== requestId) {
            try {
              currentViewer.unloadModel(modelEntry.id)
            } catch {
              // Ignore unload failures for outdated model.
            }
            return
          }

          currentModelIdRef.current = modelEntry.id
          setViewerFileFormat(modelEntry.format)

          applyTopologyMode(currentViewer)
          applyInitialSettings(currentViewer, settings, images, material, openedModelId, isSmartLowPoly)
          requestAnimationFrame(() => {
            if (currentLoadRequestIdRef.current === requestId) {
              setIsInitialApplyPending(false)
            }
          })
        })
        .catch(() => {
          if (currentLoadRequestIdRef.current === requestId) {
            // Ignore load failure; viewer will attempt to load again on next update.
            setIsInitialApplyPending(false)
          }
        })
    }).catch(() => {})
  }, [
    viewer,
    modelData.url,
    modelData.pngUrl,
    modelData.mtlUrl,
    modelData.format,
  ])

  useEffect(() => {
    if (!isEnvironmentSettingsOpen) {
      return undefined
    }

    const controller = new AbortController()
    const { signal } = controller

    document.addEventListener('mousedown', (event) => {
      if (event.target.closest('.environment-settings')) {
        return
      }
      // The Bulb trigger toggles the popover itself; excluding it avoids a
      // close-then-reopen race between this listener and the button's onClick.
      if (event.target.closest('#inner-viewer-sidebar-bulb-button')) {
        return
      }

      setIsEnvironmentSettingsOpen(false)
    }, { capture: true, signal })

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        setIsEnvironmentSettingsOpen(false)
      }
    }, { signal })

    return () => controller.abort()
  }, [isEnvironmentSettingsOpen])

  return (
    <>
      <RenderModeSelector />
      <ModelDetailsCard />
      <EnvironmentSettings isOpen={isEnvironmentSettingsOpen} />
      <InnerViewerSidebar
        isEnvironmentSettingsOpen={isEnvironmentSettingsOpen}
        onEnvironmentSettingsClick={handleEnvironmentSettingsClick}
      />
      {isInitialApplyPending && <LoadingState />}
    </>
  )
}

export default ViewerContent
